import React, { useState, useEffect } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import axios from 'axios';
import AnimatedCounter from './AnimatedCounter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const [waitlistCount, setWaitlistCount] = useState(1247);

  useEffect(() => {
    fetchWaitlistCount();
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/waitlist/count`);
      setWaitlistCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch waitlist count:', error);
    }
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium animate-fade-in">
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
            Early Access Available
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight animate-slide-in-left px-4" style={{animationDelay: '0.1s', opacity: 0}}>
            <span>
              Learn&nbsp;
              <span className="block sm:inline">System Design</span>
            </span>
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              By Building Real Systems
            </span>
          </h1>

          {/* Supporting text */}
          <p className="text-base sm:text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed animate-fade-in px-4" style={{animationDelay: '0.3s', opacity: 0}}>
            Master real constraints, trade-offs, and capacity planning.
            <br className="hidden sm:block" />
            <span className="sm:inline"> </span>Practice interview-grade thinking, not just theory.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-scale-in" style={{animationDelay: '0.5s', opacity: 0}}>
            <button
              onClick={scrollToWaitlist}
              className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/50 hover:scale-105 flex items-center gap-2"
            >
              <span className="hidden sm:inline">Join the Early Access Waitlist</span>
              <span className="inline sm:hidden">Join the Waitlist</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToDemo}
              className="group px-8 py-4 bg-gray-800/50 hover:bg-gray-800 text-white font-semibold rounded-lg border border-gray-700 transition-all duration-300 hover:border-cyan-500/50 flex items-center gap-2"
            >
              <Play className="w-5 h-5" />
              Watch the Demo
            </button>
          </div>

          {/* Social proof counter */}
          <div className="pt-8 animate-fade-in" style={{animationDelay: '0.7s', opacity: 0}}>
            <p className="text-gray-500 text-sm">
              Join <span className="text-cyan-400 font-bold text-lg">
                <AnimatedCounter value={waitlistCount} />
              </span> engineer{waitlistCount !== 1 ? 's' : ''} already on the waitlist
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
